import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadCloud, FileText, Trash2, Download, Loader2, FolderOpen, ClipboardList, Check, CalendarDays, Video, Plane, Stethoscope, CreditCard, Syringe, Sparkles, RefreshCw, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { SEO } from "../components/SEO";
import { ConsentCheckboxes } from "../components/ConsentDocs";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { api, formatApiError } from "../lib/api";
import { JapanMascot } from "../components/JapaneseMotifs";

function prettySize(n) {
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

const WA_URL = "https://wa.me/905383085160?text=" + encodeURIComponent("Merhaba, raporumun özeti hakkında konuşmak istiyorum.");

function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i} className="text-ink font-semibold">{p.slice(2, -2)}</strong> : p));
}

export function SummaryText({ text }) {
  const lines = String(text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="mt-1.5 space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-ink/80 leading-relaxed">
              <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
              <span>{renderInline(b)}</span>
            </li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((ln, i) => {
    const s = ln.trim();
    if (!s) { flush(); return; }
    if (s.startsWith("###")) {
      flush();
      out.push(<h4 key={i} className="mt-4 first:mt-0 font-heading text-sm font-bold text-ink border-b border-brand/15 pb-1">{s.replace(/^#+\s*/, "")}</h4>);
    } else if (/^[-•*]\s+/.test(s)) {
      bullets.push(s.replace(/^[-•*]\s+/, ""));
    } else if (/^-{3,}|─{3,}$/.test(s)) {
      flush();
    } else {
      flush();
      out.push(<p key={i} className="mt-2 text-sm text-ink/80 leading-relaxed">{renderInline(s)}</p>);
    }
  });
  flush();
  return <div className="min-w-0" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>{out}</div>;
}

const APPT_KIND_UI = {
  zoom: { icon: Video, cls: "bg-brand/10 text-brand" },
  visit: { icon: Stethoscope, cls: "bg-emerald-500/10 text-emerald-600" },
  travel: { icon: Plane, cls: "bg-amber-500/10 text-amber-600" },
  payment: { icon: CreditCard, cls: "bg-rose-500/10 text-rose-600" },
  dose: { icon: Syringe, cls: "bg-violet-500/10 text-violet-600" },
  other: { icon: CalendarDays, cls: "bg-black/5 text-sub" },
};

export default function PatientPortal() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [apps, setApps] = useState([]);
  const [appts, setAppts] = useState([]);
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const [kvkk, setKvkk] = useState(false);
  const [agreement, setAgreement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [sumBusy, setSumBusy] = useState(null);
  const [summaries, setSummaries] = useState({});

  const load = useCallback(() => {
    api.get("/files/mine").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/applications/mine").then((r) => setApps(r.data)).catch(() => {});
    api.get("/appointments/mine").then((r) => setAppts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) navigate("/giris");
    else if (user) {
      load();
      if (!sessionStorage.getItem("tudi-panel-toured")) {
        sessionStorage.setItem("tudi-panel-toured", "1");
        setTimeout(() => window.dispatchEvent(new CustomEvent("tudi-say", { detail: { text: t("portal_tour_hello") } })), 1200);
      }
    }
  }, [user, navigate, load]);

  if (!user) return null;

  const upload = async () => {
    if (!pending) return;
    if (!kvkk || !agreement) {
      toast.error(t("consent_required"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pending);
      fd.append("note", note);
      fd.append("consent_kvkk", "true");
      fd.append("consent_agreement", "true");
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("upload_success"));
      window.dispatchEvent(new CustomEvent("tudi-say", { detail: { text: t("portal_tour_uploaded") } }));
      setPending(null); setNote(""); setKvkk(false); setAgreement(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("form_error")); }
  };

  const remove = async (f) => {
    await api.delete(`/files/${f.id}`).catch(() => {});
    load();
  };

  const SUMMABLE = ["pdf", "docx", "txt"];
  const canSummarize = (f) => SUMMABLE.includes((f.original_filename || "").split(".").pop().toLowerCase());
  const summarize = async (f, force = false) => {
    setSumBusy(f.id);
    try {
      const r = await api.post(`/files/${f.id}/summarize${force ? "?force=true" : ""}`);
      setSummaries((prev) => ({ ...prev, [f.id]: r.data.ai_summary }));
    } catch (err) {
      const code = err?.response?.data?.detail;
      toast.error(code === "unsupported_type" ? t("sum_error_unsupported") : code === "no_text" ? t("sum_error_notext") : formatApiError(err, t("form_error")));
    } finally {
      setSumBusy(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setPending(f);
  };

  const statusLabel = { new: t("status_new"), in_review: t("status_in_review"), contacted: t("status_contacted"), closed: t("status_closed") };

  const steps = [
    { label: t("portal_step1"), done: apps.length > 0 },
    { label: t("portal_step2"), done: files.length > 0 },
    { label: t("portal_step3"), done: apps.some((a) => ["in_review", "contacted", "closed"].includes(a.status)) },
    { label: t("portal_step4"), done: apps.some((a) => a.status === "closed") || appts.length > 0 },
  ];

  return (
    <div className="min-h-screen bg-mist">
      <SEO title={t("nav_portal")} description={t("portal_upload_hint")} path="/panel" noindex />
      <Navbar />
      <div className="pt-28 pb-24 px-6 max-w-6xl mx-auto" data-testid="patient-portal">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand/10 text-brand text-[11px] font-bold uppercase tracking-wider">{t("portal_system_title")}</div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">{t("portal_welcome")}, {user.name}</h1>
          <p className="mt-2 text-sub text-sm">{t("portal_system_sub")} <span className="text-sub/70">· {user.email}</span></p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-10 bg-white rounded-3xl border border-black/5 p-5 sm:p-6" data-testid="portal-stepper">
          <div className="flex items-start">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center flex-1 last:flex-none" data-testid={`portal-step-${i + 1}`}>
                <div className="flex flex-col items-center gap-1.5 text-center w-14 sm:w-auto">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${s.done ? "bg-emerald-500 text-white" : "bg-mist text-sub border border-black/10"}`}>
                    {s.done ? <Check size={14} /> : i + 1}
                  </div>
                  <span className={`text-[10px] sm:text-[11px] font-semibold leading-tight ${s.done ? "text-emerald-600" : "text-sub"}`}>{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1.5 sm:mx-3 mt-[-14px] rounded ${s.done ? "bg-emerald-400" : "bg-black/10"}`} />}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10 bg-white rounded-3xl border border-black/5 p-6 sm:p-7" data-testid="portal-plan">
          <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><CalendarDays size={19} className="text-brand" /> {t("portal_plan_title")}</h2>
          {appts.length === 0 ? (
            <p className="mt-3 text-sm text-sub flex items-center gap-3" data-testid="portal-plan-empty"><JapanMascot className="w-10 opacity-80" /> {t("portal_plan_empty")}</p>
          ) : (
            <div className="mt-4 space-y-2.5">
              {appts.map((a) => {
                const ui = APPT_KIND_UI[a.kind] || APPT_KIND_UI.other;
                const KindIcon = ui.icon;
                return (
                  <div key={a.id} data-testid={`appt-row-${a.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${ui.cls}`}><KindIcon size={16} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink flex flex-wrap items-center gap-2">
                        {a.title}
                        {a.amount && <span className="text-xs font-bold text-ink/60">{a.amount}</span>}
                        {a.status && <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${a.status === "paid" || a.status === "done" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>{t(`appt_status_${a.status}`)}</span>}
                      </div>
                      <div className="text-[11px] text-sub">{t(`appt_${a.kind}`)} · {new Date(a.date).toLocaleString()}{a.note ? ` · ${a.note}` : ""}</div>
                    </div>
                    {a.link && <a href={a.link} target="_blank" rel="noreferrer" data-testid={`appt-join-${a.id}`} className="h-8 px-4 rounded-full bg-brand text-white text-[11px] font-bold flex items-center gap-1.5 hover:bg-branddeep transition-colors"><Video size={12} /> {t("appt_join")}</a>}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl border border-black/5 p-7" data-testid="upload-card">
            <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><UploadCloud size={19} className="text-brand" /> {t("portal_upload_title")}</h2>
            <p className="mt-1.5 text-xs text-sub">{t("portal_upload_hint")}</p>

            <div
              data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById("portal-file-input").click()}
              className={`mt-5 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors duration-200 ${drag ? "border-brand bg-brand/5" : "border-black/15 hover:border-brand/50"}`}
            >
              <FolderOpen size={28} className="mx-auto text-sub" />
              <p className="mt-3 text-sm text-ink/70 font-medium">{pending ? pending.name : t("portal_drop")}</p>
              <input id="portal-file-input" data-testid="upload-file-input" type="file" className="hidden" onChange={(e) => e.target.files?.[0] && setPending(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png,.webp,.dcm,.txt,.doc,.docx" />
            </div>

            <input data-testid="upload-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("portal_note")} className="mt-4 w-full h-11 px-4 rounded-2xl bg-mist border border-black/10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" />

            <div className="mt-4">
              <ConsentCheckboxes kvkk={kvkk} agreement={agreement} onKvkk={setKvkk} onAgreement={setAgreement} />
            </div>

            <button data-testid="upload-submit" onClick={upload} disabled={!pending || busy} className="mt-5 w-full h-12 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />} {busy ? t("portal_uploading") : t("portal_upload_btn")}
            </button>
          </motion.section>

          <div className="space-y-6">
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl border border-black/5 p-7" data-testid="my-files-card">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><FileText size={19} className="text-brand" /> {t("portal_myfiles")}</h2>
              <div className="mt-4 space-y-2.5">
                {files.length === 0 && (
                  <div className="py-6 text-center" data-testid="files-empty">
                    <JapanMascot className="w-16 mx-auto opacity-80" />
                    <p className="text-sm text-sub mt-2">{t("portal_empty_files")}</p>
                  </div>
                )}
                {files.map((f) => (
                  <div key={f.id} data-testid={`file-row-${f.id}`} className="rounded-2xl border border-black/5 bg-mist/60 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <FileText size={17} className={`shrink-0 ${f.locked ? "text-emerald-600" : "text-brand"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink truncate flex items-center gap-2">{f.original_filename}{f.locked && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">{t("portal_signed")}</span>}</div>
                        <div className="text-[11px] text-sub">{prettySize(f.size)} · {new Date(f.created_at).toLocaleDateString()}{f.note ? ` · ${f.note}` : ""}</div>
                      </div>
                      {canSummarize(f) && !(summaries[f.id] || f.ai_summary) && (
                        <button data-testid={`file-summarize-${f.id}`} onClick={() => summarize(f)} disabled={sumBusy === f.id} className="h-8 px-3 rounded-full bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-wider hover:bg-brand hover:text-white disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1.5">
                          {sumBusy === f.id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {sumBusy === f.id ? t("sum_loading") : t("sum_btn")}
                        </button>
                      )}
                      {(summaries[f.id] || f.ai_summary) && (
                        <button data-testid={`file-resummarize-${f.id}`} onClick={() => summarize(f, true)} disabled={sumBusy === f.id} title={t("sum_regenerate")} className="h-8 px-2.5 rounded-full bg-black/5 text-sub hover:bg-brand hover:text-white disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1">
                          {sumBusy === f.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                          <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">{t("sum_regenerate")}</span>
                        </button>
                      )}
                      <button data-testid={`file-download-${f.id}`} onClick={() => download(f)} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center text-sub hover:text-ink" title={t("portal_download")}><Download size={15} /></button>
                      {!f.locked && <button data-testid={`file-delete-${f.id}`} onClick={() => remove(f)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-sub hover:text-red-500" title={t("portal_delete")}><Trash2 size={15} /></button>}
                    </div>
                    {(summaries[f.id] || f.ai_summary) && (
                      <div className="px-4 pb-4" data-testid={`file-summary-${f.id}`}>
                        <div className="rounded-xl bg-white border border-brand/15 p-4">
                          <div className="text-[10px] font-heading font-bold uppercase tracking-wider text-brand flex items-center gap-1.5"><Sparkles size={11} /> {t("sum_title")}</div>
                          <div className="mt-1"><SummaryText text={summaries[f.id] || f.ai_summary} /></div>
                          <div className="mt-4 pt-3 border-t border-black/5 flex flex-wrap gap-2">
                            <a data-testid={`summary-wa-${f.id}`} href={WA_URL} target="_blank" rel="noreferrer" className="h-9 px-4 rounded-full bg-[#25D366] text-white text-xs font-bold flex items-center gap-1.5 hover:opacity-90 transition-opacity">
                              <MessageCircle size={13} /> {t("sum_cta_wa")}
                            </a>
                            <button data-testid={`summary-apply-${f.id}`} onClick={() => navigate("/basvuru")} className="h-9 px-4 rounded-full bg-brand text-white text-xs font-bold flex items-center gap-1.5 hover:bg-branddeep transition-colors">
                              <Send size={13} /> {t("sum_cta_apply")}
                            </button>
                            <button data-testid={`summary-chat-${f.id}`} onClick={() => window.dispatchEvent(new CustomEvent("open-tudi-chat"))} className="h-9 px-4 rounded-full border border-brand/30 text-brand text-xs font-bold flex items-center gap-1.5 hover:bg-brand/5 transition-colors">
                              <Sparkles size={13} /> {t("sum_cta_chat")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>

            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-3xl border border-black/5 p-7" data-testid="my-apps-card">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><ClipboardList size={19} className="text-brand" /> {t("portal_myapps")}</h2>
              <div className="mt-4 space-y-2.5">
                {apps.length === 0 && (
                  <div className="py-6 text-center" data-testid="apps-empty">
                    <JapanMascot className="w-16 mx-auto opacity-80" />
                    <p className="text-sm text-sub mt-2">{t("portal_empty_apps")}</p>
                  </div>
                )}
                {apps.map((a) => (
                  <div key={a.id} data-testid={`app-row-${a.id}`} className="rounded-2xl border border-black/5 bg-mist/60 px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{a.treatment || a.kind}</div>
                      <div className="text-[11px] text-sub">{new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${a.status === "new" ? "bg-brand/10 text-brand" : a.status === "closed" ? "bg-black/10 text-sub" : "bg-emerald-500/10 text-emerald-600"}`}>{statusLabel[a.status] || a.status}</span>
                  </div>
                ))}
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
}
